import { Extension, generateText, Node } from "@tiptap/core";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import { default as classnames, default as cn } from "classnames";
import isEqual from "lodash/isEqual";
import { useEffect, useState } from "react";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip/index.tsx";
import { TiptapErrorBoundary } from "src/components/TiptapErrorBoundary";
import VisualizeEntities from "../../../../src/shared/visualizeEntities";
import GenerateScenesLoaderButton from "../TiptapDocumentSB/Draft/GenerateScenesLoaderButton";
import { navigateToEntityMention } from "../PersistentChat/mentions/navigateToEntityMention";
import { handleEntityLinkClick, handleEntityLinkMouseDown } from "./entityNavigation";
import { serializeListFieldSliceToNumberedText, stripLeadingListMarker } from "./listFieldText";
import { placeholders } from "./placeholders";
import { Container } from "./styles";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../StickyTour/TourTypes";
import { useStickyTourStore } from "../../stores/stickyTourStore";

const CustomDocument = Node.create({
  name: "customDocument",

  content: "customOrderedList",
  topNode: "customOrderedList",

  parseHTML() {
    return [{ tag: "div.custom-schema" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", { class: "custom-schema", ...HTMLAttributes }, 0];
  },
});

const CustomOrderedList = Node.create({
  name: "customOrderedList",

  content: "customListItem+",

  parseHTML() {
    return [{ tag: "ol" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["ol", HTMLAttributes, 0];
  },
});

const CustomListItem = Node.create({
  name: "customListItem",

  content: "text*",

  parseHTML() {
    // Rewrite responses contain escaped plain text. Preserve whitespace inside
    // each item without treating HTML indentation between items as scene text.
    return [{ tag: "li", preserveWhitespace: "full" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["li", HTMLAttributes, 0];
  },
});

const pasteAsList = new PluginKey("pasteAsListPlugin");

const PastAsList = Extension.create({
  name: "pastAsList",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pasteAsList,
        props: {
          handleKeyDown: (view, event) => {
            this.shiftKeyPressed = event.shiftKey;
          },
          handleKeyUp: (view, event) => {
            this.shiftKeyPressed = event.shiftKey;
          },
          handlePaste: (view, event) => {
            if (this.shiftKeyPressed) {
              return false;
            }

            const { clipboardData } = event;
            const text = clipboardData.getData("text/plain");

            const lines = text.split("\n");

            if (!text) return false;

            const listItems = lines
              .filter((line) => line.trim())
              .map((line) => `<li>${stripLeadingListMarker(line)}</li>`)
              .join("");

            this.editor.commands.insertContent(listItems);

            return true;
          },
        },
      }),
    ];
  },
});

/** Scene-line text fingerprint for Draft lists — ignores marks so server/agent updates still sync. */
function scenesJsonFingerprint(json) {
  if (!json || typeof json !== "object") return "";
  const docContent = json.content;
  if (!Array.isArray(docContent)) return "";
  const orderedList = docContent.find((n) => n.type === "customOrderedList");
  if (!orderedList?.content || !Array.isArray(orderedList.content)) return "";
  return orderedList.content
    .filter((item) => item.type === "customListItem")
    .map((item) =>
      (Array.isArray(item.content) ? item.content : [])
        .filter((c) => c && c.type === "text" && typeof c.text === "string")
        .map((c) => c.text)
        .join(""),
    )
    .join("\x1e");
}

function TiptapListField({
  //
  onChange,
  value,
  className,
  readOnly,
  updateValueString,
  generate,
  generateLabel,
  generateDisabled,
  setLastInvocation,
  requesting,
  outOfQuota,
  entityNames,
  disabled,
  tooltipContent,
  // Error tracking props
  userId,
  projectId,
}) {
  const [isEmpty, setIsEmpty] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [dirtyTracking, setDirtyTracking] = useState(false);
  const [hasMultipleItems, setHasMultipleItems] = useState(false);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  // Helper function to check if editor has meaningful content
  const hasValidContent = (editor) => {
    if (!editor) {
      return false;
    }

    const json = editor.getJSON();
    if (!json || !json.content) {
      return false;
    }

    // Find the ordered list
    const orderedList = json.content.find((node) => node.type === "customOrderedList");
    if (!orderedList || !orderedList.content) {
      return false;
    }

    const listItems = orderedList.content.filter((item) => item.type === "customListItem");

    // If there's exactly one list item and it's empty, consider it as "empty" (show generate button)
    if (listItems.length === 1) {
      const firstItem = listItems[0];

      if (!firstItem.content || firstItem.content.length === 0) {
        return false; // Single empty item = show generate button
      }

      // Check if the single item only contains empty text
      const hasOnlyEmptyText = firstItem.content.every((textNode) => {
        return textNode.type === "text" && (!textNode.text || textNode.text.trim().length === 0);
      });

      return !hasOnlyEmptyText; // If only empty text, show generate button
    }

    // If there are multiple list items, hide the generate button regardless of content
    if (listItems.length > 1) {
      return true; // Multiple items = hide generate button
    }

    return false;
  };

  // Helper function to check if there are multiple list items
  const hasMultipleListItems = (editor) => {
    if (!editor) return false;

    const json = editor.getJSON();
    if (!json || !json.content) return false;

    const orderedList = json.content.find((node) => node.type === "customOrderedList");
    if (!orderedList || !orderedList.content) return false;

    const listItems = orderedList.content.filter((item) => item.type === "customListItem");
    return listItems.length > 1;
  };

  const showGenerateButton = isEmpty && !readOnly && !hasMultipleItems;

  const editor = useEditor({
    extensions: [
      CustomDocument,
      CustomListItem,
      CustomOrderedList,
      Text,
      Paragraph,
      History,
      PastAsList,
      Placeholder.configure({
        placeholder: `Write or generate a list of the scenes in this Chapter. For best results, include details like which characters are in the scene, where it occurs, how it begins, and how it ends.

${placeholders[Math.floor(Math.random() * placeholders.length)]}`,
        includeChildren: true,
      }),
      VisualizeEntities.configure(),
    ],
    content: value,
    editable: !readOnly,
    editorProps: {
      handleDOMEvents: {
        mousedown: (_view, event) => handleEntityLinkMouseDown(event),
        click: (_view, event) => handleEntityLinkClick(event, navigateToEntityMention),
      },
      // text/plain flavor for Cmd+C selection copies: "1. " markers so plain
      // targets (Cursor, Codex, terminals) see a numbered list, matching the
      // text/html <ol> that rich targets already get.
      clipboardTextSerializer: (slice) => serializeListFieldSliceToNumberedText(slice),
    },
    onUpdate: ({ editor, transaction }) => {
      if (readOnly) return;
      const json = editor.getJSON();
      if (onChange) {
        // if the changes only include marks then it's not a user edit, so we don't want to clear the last invocation
        const isOnlyMarkChanges = transaction.steps.every((step) => step.jsonID === "removeMark" || step.jsonID === "addMark");
        const shouldClearLastInvocation = !isOnlyMarkChanges;

        onChange(json, shouldClearLastInvocation);
      }
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
    },
    onBlur: ({ editor }) => {
      setIsFocused(false);
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
    },
    onFocus: ({ editor }) => {
      setIsFocused(true);
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
    },
    onCreate: ({ editor }) => {
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
    },
  });

  useEffect(() => {
    // NOTE: updateValueString could be a string or json object already...
    if (updateValueString && editor.commands) {
      editor.commands.setContent(updateValueString);
      const json = editor.getJSON();
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
      if (onChange) {
        onChange(json, true);
      }
    }
  }, [updateValueString]);

  useEffect(() => {
    if (!editor || readOnly) return;
    if (!value || typeof value !== "object") return;
    // SW-9359: while the editor is focused the user is actively typing, and the
    // incoming `value` is almost certainly a late echo of our own debounced save
    // that's now stale relative to the local doc. Re-applying it via setContent
    // would replace the document and snap the caret to the end ("cursor jumps
    // to bottom"). External-source updates don't need this path: the agent flow
    // remounts the field via a key bump, and history restore uses
    // `updateValueString`.
    if (editor.isFocused) return;
    let currentJson;
    try {
      currentJson = editor.getJSON();
    } catch {
      return;
    }
    if (isEqual(currentJson, value)) return;
    if (scenesJsonFingerprint(currentJson) === scenesJsonFingerprint(value)) return;

    try {
      editor.commands.setContent(value, false);
      const hasContent = hasValidContent(editor);
      setIsEmpty(!hasContent);
      setHasMultipleItems(hasMultipleListItems(editor));
    } catch {
      /* ignore invalid server/agent JSON */
    }
  }, [editor, readOnly, value]);

  useEffect(() => {
    if (editor && entityNames && !isEqual(dirtyTracking, { value, entityNames })) {
      editor.commands.setEntities({ entities: entityNames });

      setDirtyTracking({ value, entityNames });
    }
  }, [editor, entityNames, value, dirtyTracking]);

  return (
    <TiptapErrorBoundary
      editorName="TiptapListField"
      userId={userId}
      projectId={projectId}
    >
      <Container
        className={classnames("tip-tap-ordered-list-field-container", {
          [className]: className,
          "has-content": !isEmpty,
        })}
      >
        <EditorContent editor={editor} />
        {showGenerateButton ? (
          <>
            {generateDisabled ? (
              <GenerateScenesLoaderButton
                requesting={requesting}
                outOfQuota={outOfQuota}
                disabled={generateDisabled}
                onClick={() => {}}
              />
            ) : (
              <div className="">
                <div className="-ml-[1.7em] -mb-[0.375em]">
                  <StickyNoteAnchor anchorId={AnchorIds.DRAFT_SCENES_GENERATE} />
                  <SudoTooltip
                    tooltipClassName="w-[32rem] max-w-[90vw] p-4"
                    customContent={tooltipContent}
                    hasArrow={false}
                  >
                    <SudoButton
                      id="button-generate-scenes"
                      variant="tertiary"
                      className={cn("fill-app-primary-cta-high-contrast")}
                      leftIcon="/assets/AI-sparkle.svg"
                      onClick={() => {
                        if (requesting || outOfQuota || generateDisabled) return;
                        sendFlowSignal(TourSignal.DRAFT_SCENES_GENERATE_CLICKED);
                        if (generate()) {
                          setLastInvocation(null);
                        }
                      }}
                      disabled={requesting || outOfQuota || generateDisabled || disabled}
                    >
                      {generateLabel}
                    </SudoButton>
                  </SudoTooltip>
                </div>
              </div>
            )}
          </>
        ) : null}
      </Container>
    </TiptapErrorBoundary>
  );
}

export default TiptapListField;

function pruneEmptyTextNodes(node) {
  if (Array.isArray(node)) {
    return node.map(pruneEmptyTextNodes);
  }
  if (typeof node === "object" && node !== null) {
    const { content, ...rest } = node;

    return {
      ...rest,
      ...(content
        ? {
            content: content.map(pruneEmptyTextNodes).filter((child) => !(child.type === "text" && child.text === "")),
          }
        : {}),
    };
  }
  return node;
}

export const getTextFromTiptapListField = (json) => {
  if (!json) return "";

  // Remove any empty text nodes since ProseMirror doesn't allow them
  const cleanedJson = pruneEmptyTextNodes(json);

  const text = generateText(cleanedJson, [CustomDocument, CustomListItem, CustomOrderedList, Text, VisualizeEntities]);

  return text;
};

export { getNumberedTextFromTiptapListField, stripLeadingListMarker } from "./listFieldText";

export const oldBeatsToTiptap = (beats) => {
  const beatsContent = beats.contents;

  return {
    type: "customDocument",
    content: [
      {
        type: "customOrderedList",
        content: [
          {
            type: "customListItem",
            content: [
              {
                type: "text",
                text: beatsContent,
              },
            ],
          },
        ],
      },
    ],
  };
};
